import { useCallback, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, SectionTitle, Field, FormError, SubmitButton, StatusBadge } from "@/components/common";
import { SlotPicker, fmtTime, fmtDayLong } from "@/modules/scheduling/shared";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtYmd, fmtNumber } from "@/lib/format";
import { t } from "@/i18n";

export function DueText({ due, className = "" }) {
  return (
    <span className={`block text-xs ${className}`} data-testid="due-text">
      {due.due_date && <span className={`block ${due.date_state === "overdue" ? "font-semibold text-red-600" : due.date_state === "due_soon" ? "text-amber-700" : "text-slate-600"}`}>{t("fu.dueDate", { date: fmtYmd(due.due_date) })}</span>}
      {due.due_km && <span className="block text-slate-600">{due.last_odometer != null
        ? t("fu.dueKm", { km: fmtNumber(due.due_km), odo: fmtNumber(due.last_odometer), date: due.last_odometer_date ? fmtYmd(due.last_odometer_date) : "—" })
        : t("fu.dueKmNoReading", { km: fmtNumber(due.due_km) })}</span>}
    </span>
  );
}

export const slotLabel = (iso, tz) => `${fmtDayLong(iso, tz)} ${fmtTime(iso, tz)}`;

function ServiceSelect({ value, onChange }) {
  const { data } = useFetch("/garage/services");
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid="fu-service-select"><SelectValue placeholder={t("fu.pickService")} /></SelectTrigger>
      <SelectContent>{(data || []).filter((s) => s.active !== false).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function NewMaintenanceDialog({ jobId, customerId, vehicleId, onClose, onSaved }) {
  const [f, setF] = useState({ due_date: "", due_km: "", recommendation: "", service_id: "", reminders_enabled: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/followups/maintenance", { job_id: jobId || null, customer_id: customerId || null, vehicle_id: vehicleId || null, recommendation: f.recommendation,
        due_date: f.due_date || null, due_km: f.due_km ? Number(f.due_km) : null, service_id: f.service_id || null, reminders_enabled: f.reminders_enabled });
      toast.success(t("common.saved"));
      onSaved();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-maintenance-dialog">
        <DialogHeader><DialogTitle>{t("fu.setNext")}</DialogTitle><DialogDescription>{t("fu.setNextHint")}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <FormError message={error} />
          <Field label={t("fu.recommendation")}><Textarea required value={f.recommendation} onChange={(e) => setF({ ...f, recommendation: e.target.value })} data-testid="maintenance-recommendation-input" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("fu.dueDateLabel")}><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="maintenance-due-date-input" /></Field>
            <Field label={t("fu.dueKmLabel")}><Input type="number" min={1} value={f.due_km} onChange={(e) => setF({ ...f, due_km: e.target.value })} data-testid="maintenance-due-km-input" /></Field>
          </div>
          <Field label={t("fu.suggestedService")}><ServiceSelect value={f.service_id} onChange={(v) => setF({ ...f, service_id: v })} /></Field>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.reminders_enabled} onCheckedChange={(v) => setF({ ...f, reminders_enabled: v })} data-testid="maintenance-reminders-switch" /> {t("fu.scheduleReminders")}</label>
          <SubmitButton busy={busy} data-testid="maintenance-save-button">{t("common.save")}</SubmitButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ProposeDialog({ item, onClose, onSaved }) {
  const { garage } = useGarage();
  const [service, setService] = useState(item.service_id || "");
  const [picked, setPicked] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback((day) => api.get("/booking/slots", { params: { service_id: service, day } }).then((r) => r.data), [service]);
  const toggle = (slot) => setPicked((p) => (p.includes(slot.start) ? p.filter((x) => x !== slot.start) : p.length < 3 ? [...p, slot.start] : p));
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/followups/maintenance/${item.id}/proposals`, { service_id: service, starts: picked, note });
      toast.success(t("fu.proposed"));
      onSaved();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="propose-dialog">
        <DialogHeader><DialogTitle>{t("fu.propose")}</DialogTitle><DialogDescription>{t("fu.proposeHint")}</DialogDescription></DialogHeader>
        <FormError message={error} />
        <Field label={t("sched.service")}><ServiceSelect value={service} onChange={(v) => { setService(v); setPicked([]); }} /></Field>
        {service && <SlotPicker load={load} tz={garage.timezone} value={null} onChange={toggle} testPrefix="propose-slot" />}
        <div className="flex flex-wrap gap-2" data-testid="propose-picked">{picked.map((s) => <button key={s} type="button" onClick={() => setPicked(picked.filter((x) => x !== s))} className="rounded-full bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-800">{slotLabel(s, garage.timezone)} ×</button>)}</div>
        <Field label={t("fu.note")}><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="propose-note-input" /></Field>
        <div className="gf-modal-footer">
        <Button onClick={submit} disabled={busy || !picked.length} data-testid="propose-submit-button">{t("fu.sendProposal", { n: picked.length })}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ProposalSummary({ p, tz }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`proposal-${p.id}`}>
      <div className="flex items-center justify-between"><strong>{p.service_name}</strong><StatusBadge status={p.status} /></div>
      <ul className="mt-1 text-xs text-slate-600">{p.slots.map((s) => <li key={s}>{slotLabel(s, tz)}</li>)}</ul>
      {p.response?.comment && <p className="mt-1 text-xs italic text-slate-700">“{p.response.comment}”</p>}
    </div>
  );
}

export function JobMaintenanceCard({ job }) {
  const { garage, isManager, hasFeature, readOnly } = useGarage();
  const show = isManager && hasFeature("follow_ups") && ["ready", "completed"].includes(job.status);
  const { data, reload } = useFetch(show ? "/followups" : null, { job_id: job.id });
  const [dlg, setDlg] = useState(null);
  if (!show) return null;
  return (
    <Card testId="job-maintenance-card">
      <SectionTitle hint={t("fu.jobHint")}>{t("fu.nextMaintenance")}</SectionTitle>
      <div className="space-y-3">
        {(data?.items || []).map((i) => (
          <div key={i.id} className="space-y-2 rounded-lg border border-slate-200 p-3 text-sm" data-testid={`job-maintenance-${i.id}`}>
            <div className="flex items-center justify-between gap-2"><strong>{i.recommendation}</strong><StatusBadge status={i.status} /></div>
            <DueText due={i.due} />
            {i.proposals.slice(0, 1).map((p) => <ProposalSummary key={p.id} p={p} tz={garage.timezone} />)}
            {!readOnly && i.status === "open" && <Button size="sm" variant="outline" onClick={() => setDlg({ propose: i })} data-testid="job-propose-button">{t("fu.propose")}</Button>}
          </div>
        ))}
        {!readOnly && <Button size="sm" onClick={() => setDlg({ create: true })} data-testid="job-set-maintenance-button">{t("fu.setNext")}</Button>}
      </div>
      {dlg?.create && <NewMaintenanceDialog jobId={job.id} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); reload(); }} />}
      {dlg?.propose && <ProposeDialog item={dlg.propose} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); reload(); }} />}
    </Card>
  );
}
